import React, { useCallback, useEffect, useState } from 'react';
import { getQuotePdfFromHtml, QuoteApproval } from '../../Services/PtaasApiService';
import { useMsal } from '@azure/msal-react';
import QuotePDF from '../Overview/ProjectQuotePDF';
import SignatureData from '../../Models/Signature';
import SignatureModal from '../Global/SignatureModal';
import { LoaderIcon } from '../Global/Icons';
import AccessRestricted from '../Global/UnauthorizedMessage';

interface SignatureProps {
    testid: string;
    // Whether this quote can be signed at all, independent of whether it already has been.
    // A retest quote is signable only while its retest sits at 'Quote Available' — the server
    // rejects anything else (QuotationSignatureService), and a cancelled retest's quote stays
    // unsigned forever, so offering Sign on it would only ever produce an error.
    signable?: boolean;
    // Signing accepts the quote, which moves the project's status — and, for a retest, cascades
    // that retest to In Progress. The page around this needs to re-read both.
    onSigned?: () => void;
    // Distinguishes this quote's download from the others on the page.
    filename?: string;
}

const QuotationPdf: React.FC<SignatureProps> = ({ testid, signable = true, onSigned, filename }) => {
    const [showSignaturePad, setShowSignaturePad] = useState(false);
    const { instance, accounts } = useMsal();
    const [quotepdfurl, setQuotepdfUrl] = useState('');
    const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [issigned, setisSigned] = useState(false);


    const fetchQuotepdf = useCallback(async (): Promise<void> => {
        setLoading(true);
        setError(null);
        try {
            const response = await getQuotePdfFromHtml(testid, instance, accounts[0]);
            const pdfUrl = window.URL.createObjectURL(response?.pdfBlob);
            setQuotepdfUrl(pdfUrl);
            setPdfBlob(response?.pdfBlob);
            setisSigned(response?.isSigned);
        } catch (error: any) {
            // Handle 404 from error message string (e.g., "404: Not Found")
            const message = error?.message || '';
            if (typeof message === 'string' && message.trim().startsWith('404')) {
                setError('The document is not available for this project.');
            } else {
                setError('Error fetching the PDF. Please try again.');
            }
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    }, [accounts, instance, testid]);

    useEffect(() => {
        fetchQuotepdf();
    }, [fetchQuotepdf]);

    const handleSignatureSubmit = async (signedQuote: SignatureData) => {
        setLoading(true);
        try {
            const response = await QuoteApproval(instance, accounts[0], signedQuote.testid, signedQuote.name, signedQuote.date, signedQuote.position, signedQuote.issigned, signedQuote.signature, signedQuote.accountsContactName, signedQuote.accountsEmail, signedQuote.purchaseOrderNumber);
            if (response) {
                setShowSignaturePad(false);
                await fetchQuotepdf();
                // After this quote's own state is refreshed, not before: the page's refetch may
                // re-render this component, and the local isSigned should already be correct.
                onSigned?.();
            }
        } catch (error: any) {
            if (typeof error?.message === 'string' && error.message.includes('Quote is not available yet for this test')) {
                setError('The quote is currently being revised. Please check back later');
            } else {
                setError('Error fetching the PDF. Please try again.');
            }
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };
    if (loading) {
        return <LoaderIcon />;
    }
    return (
        <>
            {error ? (
                <div style={{  justifyContent: 'center', display: 'flex'}}>
                    <AccessRestricted title=''
                        subTitle='Not Available'
                        message={error}
                        subMessage='If you have any concerns, please contact your administrator.'></AccessRestricted>
              </div>
            ) : (
                !loading && (
                    <>
                        <div className='text-end'>
                            <QuotePDF testid={testid} pdfBlob={pdfBlob} isSigned={issigned} filename={filename} />
                        </div>
                        <div className="quotation-container">
                            <div className="pdf-embed" style={{ position: "relative" }}>
                                <iframe
                                    title='quotation'
                                    src={`${quotepdfurl}#toolbar=0`}
                                    className="pdf-embed"
                                    style={{ visibility: loading ? "hidden" : "visible" }}
                                ></iframe>
                            </div>
                            {/* Signing IS acceptance — there is no separate Accept anywhere. An
                                already-signed quote therefore offers nothing, and neither does one
                                that is not open for signature. */}
                            {!issigned && signable && (
                                <div className="signature-overlay" onClick={() => setShowSignaturePad(true)}>
                                    Sign
                                </div>
                            )}
                            {signable && showSignaturePad && (
                                <SignatureModal
                                    setShowSignaturePad={setShowSignaturePad}
                                    onSaveSignature={handleSignatureSubmit}
                                    testid={testid} />
                            )}
                        </div>
                    </>
                )
            )}
        </>
    );

};

export default QuotationPdf;



